import { Component, HostListener, Inject, OnInit } from '@angular/core';
import {
  DwActionExecutionService,
  DwComponent,
  DwComponentType,
  DwDataSort,
  DwEventService,
  DwGridActionResult,
  DwLookupInfo,
  DwMetaDataGridActionState,
  DwMetaDataFilterTileState,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwQueryFilterGroup,
  DwQueryParam,
  DwRoutingService
} from '@devwareapps/devware-cap';
import { combineLatest, of } from 'rxjs';

import {
  AppMetaData,
  AppMetaDataLookups,
  ManageUsersQueryEntity,
  StudentByInstructorQueryEntity
} from '../../../../meta-data/app-meta-data.service';
import { AdminManagerService } from '../../../admin/services/admin-manager.service';
import { gridAnnimations } from '../../../schools/components/manage-users-grid-renderer/grid-animations.const';
import { DwAviatorGridRendererBaseComponent } from '../../../shared/components/grids/dw-aviator-grid-renderer-base.component';

type MyStudentsGridRow = StudentByInstructorQueryEntity & Partial<ManageUsersQueryEntity>;

@DwComponent({
  key: 'my-students-grid-renderer',
  name: 'My Students Grid Renderer',
  isGlobal: false,
  parentItemName: AppMetaData.DwUser.ItemDetail.itemName,
  componentType: DwComponentType.gridRenderer
})
@Component({
  selector: 'app-my-students-grid-renderer',
  templateUrl: './my-students-grid-renderer.component.html',
  styleUrls: ['./my-students-grid-renderer.component.scss'],
  animations: gridAnnimations
})
export class MyStudentsGridRendererComponent
  extends DwAviatorGridRendererBaseComponent<MyStudentsGridRow>
  implements OnInit {

  coursesLookupId = AppMetaDataLookups.CourseBySchool;
  groupsLookupId = AppMetaDataLookups.FlightSchoolGroupBySchool;

  public sortColumn: string = 'LastLoginDateTime';
  public sortDirection: 'asc' | 'desc' = 'desc';
  public selectedUserId: number | null = null;

  public showFilterPanel: boolean = false;
  public searchText: string = '';
  public successMessage = '';

  public customFilters: {
    courseId: string | null;
    groupId: string | null;
  } = {
      courseId: null,
      groupId: null
    };

  constructor(
    dwRoutingService: DwRoutingService,
    adminManagerService: AdminManagerService,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwEventService: DwEventService,
    private dwActionExecutionService: DwActionExecutionService
  ) {
    super(dwRoutingService, adminManagerService, dwMetaDataService, dwEventService);
  }

  public hasFilters: boolean = false;
  public courseOptions: DwLookupInfo[] = [];
  public groupOptions: DwLookupInfo[] = [];

  isMobile = false;
  lookupParams: (number | null | undefined)[] = [];

  noRowsConfig: {
    title: string;
    message: string;
    showAction?: boolean;
    actionText?: string;
    iconClass?: string;
    action?: () => void;
  } = {
      title: 'No students found',
      message: 'Try adjusting your search or filter criteria.',
      showAction: false,
      iconClass: 'fa fa-users'
    };

  ngOnInit(): void {
    this.checkMobile();
    this.setupLookups();
    this.configureTextSearch([AppMetaData.Queries.ManageUsers.Attributes.UserDetailUserIdDisplay], 500);
  }

  setupRenderer(): void {
    this.applyCurrentSort();
  }

  protected filterTileStateChange(filterTileState: DwMetaDataFilterTileState): void {
    if (filterTileState?.metricValue === 0) {
      this.noRowsConfig = {
        title: 'No students yet',
        message: 'Students will appear here once they are assigned to you.',
        showAction: false,
        iconClass: filterTileState.filterTileConfig?.tileIconClass || 'fa fa-users'
      };

      return;
    }

    this.noRowsConfig = {
      title: 'No students found',
      message: 'Try adjusting your search or filter criteria.',
      showAction: false,
      iconClass: filterTileState?.filterTileConfig?.tileIconClass || 'fa fa-users'
    };
  }

  setupLookups(): void {
    const aviatorUser = this.adminManagerService.getAviatorUser();

    this.lookupParams.push(aviatorUser.FlightSchoolId);
    this.loadLookupValues(aviatorUser.FlightSchoolId);
  }

  loadLookupValues(flightSchoolId?: number | null): void {
    const schoolParams: DwQueryParam[] = [{ ParamName: 'FlightSchoolId', ParamValue: flightSchoolId?.toString() || '' }];

    const courses$ = this.dwMetaDataService.getLookup(this.coursesLookupId, schoolParams) || of([]);
    const groups$ = this.dwMetaDataService.getLookup(this.groupsLookupId, schoolParams) || of([]);

    combineLatest([courses$, groups$]).subscribe(([courses, groups]) => {
      this.courseOptions = courses || [];
      this.groupOptions = groups || [];
    });
  }

  @HostListener('window:resize')
  public windowResize(): void {
    this.checkMobile();
  }

  private checkMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  public toggleFilterPanel(): void {
    this.showFilterPanel = !this.showFilterPanel;
  }

  public clearCustomFilters(): void {
    this.customFilters.courseId = null;
    this.customFilters.groupId = null;
    this.hasFilters = false;
    this.customFiltersChanged();
  }

  public searchTextChanged(): void {
    this.applyTextFilter(this.searchText);
  }

  public customFiltersChanged(): void {
    const filterGroup = new DwQueryFilterGroup();

    this.addFilter(filterGroup, this.customFilters.courseId, AppMetaData.StudentCourse.Attributes.CourseId);
    this.addFilter(filterGroup, this.customFilters.groupId, AppMetaData.FlightSchoolGroupStudent.Attributes.FlightSchoolGroupId);

    this.applyCustomFilters(filterGroup);
  }

  public toggleSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = column === 'LastLoginDateTime' ? 'desc' : 'asc';
    }

    this.applyCurrentSort();
  }

  public selectUser(user: MyStudentsGridRow): void {
    this.selectedUserId = user.UserId || null;
    this.selectedItems = [user];
  }

  public viewUser(user: MyStudentsGridRow): void {
    if (!user || !user.UserId) {
      return;
    }

    this.dwRoutingService.navigateToUrl('/manage-students/edit/' + user.UserId);
  }

  public onRowClick(user: MyStudentsGridRow): void {
    this.selectUser(user);
    this.viewUser(user);
  }

  public refresh(): void {
    this.refreshDataSource();
  }

  public addStudent(): void {
    this.successMessage = '';

    this.dwActionExecutionService.executeAction<DwMetaDataGridActionState<MyStudentsGridRow>, DwGridActionResult>(
      'instructor-add-student-by-code',
      {} as DwMetaDataGridActionState<MyStudentsGridRow>
    )
      .subscribe(result => {
        if (result?.refreshData) {
          this.successMessage = result.message?.toString() || 'The student was added to your student list.';
          this.refreshDataSource();
        }
      });
  }

  public dismissSuccessMessage(): void {
    this.successMessage = '';
  }

  public getUserMediaPath(user: MyStudentsGridRow): string {
    if (!user) {
      return '';
    }

    return user.DwroMediaMediaPath || user.UserPictureMediaIdDisplay || '';
  }

  public trackByUser(index: number, item: MyStudentsGridRow): string | number {
    return item.UserId || index;
  }

  public getFullName(user: MyStudentsGridRow): string {
    const firstName = user?.FirstName || user?.DwUserFirstName || '';
    const lastName = user?.LastName || user?.DwUserLastName || '';
    const fullName = (firstName + ' ' + lastName).trim();

    return fullName || user?.UserDetailUserIdDisplay || user?.UserIdDisplay || user?.UserName || '';
  }

  protected resolveInitialPageSize(): number {
    return 50;
  }

  protected getEffectiveSorts(): DwDataSort[] {
    if (!this.sortColumn) {
      return [];
    }

    return [
      {
        fieldName: this.sortColumn,
        ascending: this.sortDirection === 'asc'
      } as DwDataSort
    ];
  }

  private applyCurrentSort(): void {
    this.setSorts(this.getEffectiveSorts());
  }

  public onScroll(): void {
    this.nextPage();
  }
}
